import { hiDPI } from '../constants/runtime.js';
import { MediumType, MediumDensity } from '../types/media.js';
import { PLACEHOLDER_IMG, IMAGE_PLACEHOLDER_IMG, PDF_PLACEHOLDER_IMG, WORD_PLACEHOLDER_IMG, EXCEL_PLACEHOLDER_IMG, POWERPOINT_PLACEHOLDER_IMG, VIDEO_PLACEHOLDER_IMG, MODEL_3D_PLACEHOLDER_IMG, AUDIO_PLACEHOLDER_IMG, UNKNOWN_PLACEHOLDER_IMG, DOCUMENT_PLACEHOLDER_IMG } from './placeholder-data-uris.js';
/**
 * Explicit file extension to mime-type map (motivated by RAW types)
 */
export const IMAGE_EXT_TO_MIME_TYPE = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    png: 'image/png',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    bmp: 'image/x-bmp',
    ppm: 'image/x-portable-pixmap', // ???: not sure about this mime-type as none is officially registered
    dib: 'image/dib',
    webp: 'image/webp',
    heic: ['image/heic', 'image/x-heic'],
    heif: ['image/heif'],
    avif: ['image/avif'],
    svg: ['image/svg+xml'],
    psd: ['image/x-psd', 'application/x-photoshop', 'image/vnd.adobe.photoshop'],
    rtf: ['application/rtf'],
    eps: ['application/postscript'],
    dwg: ['image/x-dwg'],
    dxf: ['image/x-dxf'],
    tpc: 'image/tilepic',
    dpx: 'image/x-dpx',
    exr: 'image/x-exr',
    jp2: ['image/jp2', 'image/jpx', 'image/jpm'],
    dng: 'image/x-adobe-dng',
    cr2: 'image/x-canon-cr2',
    cr3: 'image/x-canon-cr3',
    crw: 'image/x-canon-crw',
    arw: 'image/x-sony-arw',
    orf: 'image/x-olympus-orf',
    pef: 'image/x-pentax-pef',
    erf: 'image/x-epson-erf',
    nef: 'image/x-nikon-nef',
    nrw: 'image/x-nikon-nrw',
    sr2: 'image/x-sony-sr2',
    srf: 'image/x-sony-srf',
    x3f: 'image/x-sigma-x3f',
    raw: ['image/x-panasonic-raw', 'image/x-dcraw'],
    dcm: 'application/dicom',
    dcr: 'image/x-kodak-dcr',
    k25: 'image/x-kodak-k25',
    kdc: 'image/x-kodak-kdc',
    mrw: 'image/x-minolta-mrw',
    raf: 'image/x-fuji-raf',
    rwl: 'image/x-panasonic-raw', // Panasonic RAW/RW2, also known as Leica RAW or RWL (https://mimetype.io/image/x-panasonic-raw)
    rw2: 'image/x-panasonic-rw2'
};
export function lookupExtensionForMimeType(mimeType) {
    return Object.keys(IMAGE_EXT_TO_MIME_TYPE).find((ext) => {
        const value = IMAGE_EXT_TO_MIME_TYPE[ext];
        if (Array.isArray(value)) {
            return value.includes(mimeType);
        }
        else {
            return value === mimeType;
        }
    });
}
export function inferContentTypeFromExtention(extension) {
    const probe = IMAGE_EXT_TO_MIME_TYPE[extension.toLowerCase()];
    if (!probe) {
        return '';
    }
    return Array.isArray(probe) ? probe[0] : probe;
}
/**
 * File Extensions
 */
const IMAGE_FILE_EXTENSIONS = Object.keys(IMAGE_EXT_TO_MIME_TYPE).map((ext) => `.${ext.toLowerCase()}`);
const MODEL_3D_FILE_EXTENSIONS = ['.gltf', '.glb'];
const PDF_FILE_EXTENSIONS = ['.pdf'];
const WORD_FILE_EXTENSION = ['.doc', '.docx'];
const EXCEL_FILE_EXTENSION = ['.xls', '.xlsx'];
const POWERPOINT_FILE_EXTENSION = ['.ppt', '.pptx'];
/**
 * Mime Types
 */
export const PREVIEWABLE_IMAGE_MIME_TYPES = [
    'image/gif',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/bmp',
    'image/x-bmp'
];
export const RAW_IMAGE_MIME_TYPES = [
    'image/x-dcraw',
    'image/x-adobe-dng',
    'image/x-sony-arw',
    'image/x-canon-cr2',
    'image/x-canon-cr3',
    'image/x-canon-crw',
    'image/x-kodak-dcr',
    'image/x-epson-erf',
    'image/x-kodak-k25',
    'image/x-kodak-kdc',
    'image/x-minolta-mrw',
    'image/x-nikon-nef',
    'image/x-olympus-orf',
    'image/x-pentax-pef',
    'image/x-fuji-raf',
    'image/x-panasonic-raw',
    'image/x-sony-sr2',
    'image/x-sony-srf',
    'image/x-sigma-x3f',
    'image/x-panasonic-rw2'
];
export const NONPREVIEWABLE_IMAGE_MIME_TYPES = [
    'image/tiff', // only safari supports tiff
    'image/webp', // safari doesn't support webp
    'image/jp2', // only safari supports jp2
    'image/jpx',
    'image/jpm',
    'image/x-heic',
    'image/heic',
    'image/heif',
    'image/avif',
    'image/svg+xml',
    'application/postscript',
    'image/x-psd',
    'application/x-photoshop',
    'image/vnd.adobe.photoshop',
    'image/x-dwg',
    'image/x-dxf',
    ...RAW_IMAGE_MIME_TYPES
];
export const IMAGE_MIME_TYPES = [
    ...PREVIEWABLE_IMAGE_MIME_TYPES,
    ...NONPREVIEWABLE_IMAGE_MIME_TYPES
];
export const MODEL_3D_MIME_TYPES = ['model/gltf-binary'];
export const PDF_MIME_TYPES = ['application/pdf'];
const WORD_MIME_TYPES = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const EXCEL_MIME_TYPES = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];
const POWERPOINT_MIME_TYPES = [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
];
const AUDIO_MIME_TYPE_RE = /^audio\//;
const VIDEO_MIME_TYPE_RE = /^video\//;
const IMAGE_MIME_TYPE_RE = /^image\//;
const MODEL_3D_MIME_TYPE_RE = /^model\//;
/**
 * File Specifiers (used in file open contexts)
 */
/*
export const IMAGE_FILE_SPECIFIER = ['image/*', ...IMAGE_FILE_EXTENSIONS];

export const DOCUMENT_FILE_SPECIFIER = [
  ...PDF_MIME_TYPES,
  ...WORD_MIME_TYPES,
  ...EXCEL_MIME_TYPES,
  ...POWERPOINT_MIME_TYPES,
  ...PDF_FILE_EXTENSIONS,
  ...WORD_FILE_EXTENSION,
  ...EXCEL_FILE_EXTENSION,
  ...POWERPOINT_FILE_EXTENSION
];

export const VIDEO_FILE_SPECIFIER = ['video/*', ...VIDEO_FILE_EXTENSIONS];

export const AUDIO_FILE_SPECIFIER = ['audio/*', ...AUDIO_FILE_EXTENSIONS];
*/
export const IMAGE_MIME_TYPE_TO_EXT = Object.entries(IMAGE_EXT_TO_MIME_TYPE).reduce((acc, [ext, mime]) => {
    const dottedExt = `.${ext}`;
    if (Array.isArray(mime)) {
        mime.forEach((m) => {
            if (m in acc) {
                acc[m].push(dottedExt);
            }
            else {
                acc[m] = [dottedExt];
            }
        });
    }
    else {
        // single mime
        if (mime in acc) {
            acc[mime].push(dottedExt);
        }
        else {
            acc[mime] = [dottedExt];
        }
    }
    return acc;
}, {});
export const MODEL_3D_MIME_TYPE_TO_EXT = {
    // glTF/GLB 3D models.
    'model/gltf-binary': ['.glb']
};
export const DOCUMENT_MIME_TYPE_TO_EXT = {
    // pdf
    'application/pdf': ['.pdf'],
    // word
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx'
    ],
    // excel
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
    ],
    // powerpoint
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
        '.pptx'
    ]
};
export const VIDEO_MIME_TYPE_TO_EXT = {
    // 'video/*': ['.avi', '.mp4', '.mpeg', '.ogv', '.webm', '.3gp', '.3g2']
    'video/x-msvideo': ['.avi'],
    'video/mp4': ['.mp4'],
    'video/mpeg': ['.mpeg'],
    'video/quicktime': ['.mov', '.qt'],
    'video/ogg': ['.ogv'],
    'video/webm': ['.webm'],
    'video/3gpp': ['.3gp'],
    'video/3gpp2': ['.3g2'],
    'video/x-ms-wmv': ['.wmv']
};
export const AUDIO_MIME_TYPE_TO_EXT = {
    // 'audio/*': ['.aac', '.mid', '.midi', '.mp3', '.oga', '.opus', '.wav', '.weba', '.3gp', '.3g2'],
    // 'application/*': ['.cda']
    'audio/x-m4a': ['.m4a'],
    'audio/aac': ['.aac'],
    'application/x-cdf': ['.cda'],
    'audio/midi': ['.mid', '.midi'],
    'audio/x-midi': ['.mid', '.midi'],
    'audio/mpeg': ['.mp3'],
    'audio/ogg': ['.oga'],
    'audio/opus': ['.opus'],
    'audio/wav': ['.wav'],
    'audio/webm': ['.weba'],
    'audio/3gpp': ['.3gp'],
    'audio/3gpp2': ['.3g2'],
    'audio/x-ms-wma': ['.wma']
};
export const IMPORTABLE_MIME_TYPE_TO_EXT = {
    'text/csv': ['.csv'],
    'text/plain': ['.txt'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
};
/**
 * Internal file type testers
 */
function _isImageFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (IMAGE_MIME_TYPE_RE.test(mimeType) ||
        IMAGE_FILE_EXTENSIONS.some((e) => name.endsWith(e)));
}
function _isPDFFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (PDF_MIME_TYPES.includes(mimeType) ||
        PDF_FILE_EXTENSIONS.some((e) => name.endsWith(e)));
}
function _isWordFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (WORD_MIME_TYPES.includes(mimeType) ||
        WORD_FILE_EXTENSION.some((e) => name.endsWith(e)));
}
function _isExcelFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (EXCEL_MIME_TYPES.includes(mimeType) ||
        EXCEL_FILE_EXTENSION.some((e) => name.endsWith(e)));
}
function _isPowerPointFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (POWERPOINT_MIME_TYPES.includes(mimeType) ||
        POWERPOINT_FILE_EXTENSION.some((e) => name.endsWith(e)));
}
function _isMSOffice(fileName, mimeType) {
    return (_isWordFile(fileName, mimeType) ||
        _isExcelFile(fileName, mimeType) ||
        _isPowerPointFile(fileName, mimeType));
}
function _isVideoFile(_fileName, mimeType) {
    return (VIDEO_MIME_TYPE_RE.test(mimeType) || mimeType === 'application/x-mpegURL');
}
function _is3DModelFile(fileName, mimeType) {
    const name = (fileName || '').toLowerCase();
    return (MODEL_3D_MIME_TYPE_RE.test(mimeType) ||
        MODEL_3D_FILE_EXTENSIONS.some((e) => name.endsWith(e)));
}
function _isAudioFile(_fileName, mimeType) {
    return AUDIO_MIME_TYPE_RE.test(mimeType);
}
/**
 * File type testers
 */
export function isImageContentType(contentType) {
    return IMAGE_MIME_TYPE_RE.test(contentType);
}
export function isImage(fileName, mimeType = '') {
    return _isImageFile(fileName, mimeType);
}
export function isImageFile(file) {
    return _isImageFile(file.name, file.type);
}
export function isMSOffice(fileName, mimeType) {
    return _isMSOffice(fileName, mimeType);
}
export function isMSOfficeFile(file) {
    return _isMSOffice(file.name, file.type);
}
export function isPDF(fileName, mimeType = '') {
    return _isPDFFile(fileName, mimeType);
}
export function isPDFFile(file) {
    return _isPDFFile(file.name, file.type);
}
export function isDocumentFile(file) {
    return _isPDFFile(file.name, file.type) || _isMSOffice(file.name, file.type);
}
export function isVideo(fileName, mimeType = '') {
    return _isVideoFile(fileName, mimeType);
}
export function isVideoFile(file) {
    return _isVideoFile(file.name, file.type);
}
export function is3DModel(fileName, mimeType = '') {
    return _is3DModelFile(fileName, mimeType);
}
export function is3DModelFile(file) {
    return _is3DModelFile(file.name, file.type);
}
export function isAudio(fileName, mimeType = '') {
    return _isAudioFile(fileName, mimeType);
}
export function isAudioFile(file) {
    return _isAudioFile(file.name, file.type);
}
/**
 * @deprecated Use getMediumType instead
 * File type utility testers and resolvers
 */
export function getMediaType(fileName, mimeType) {
    fileName = fileName || '';
    mimeType = mimeType || '';
    if (_isImageFile(fileName, mimeType)) {
        return MediumType.IMAGE;
    }
    else if (_isPDFFile(fileName, mimeType)) {
        return MediumType.DOCUMENT;
    }
    else if (_isMSOffice(fileName, mimeType)) {
        return MediumType.DOCUMENT;
    }
    else if (_isVideoFile(fileName, mimeType)) {
        return MediumType.VIDEO;
    }
    else if (_isAudioFile(fileName, mimeType)) {
        return MediumType.AUDIO;
    }
    else if (_is3DModelFile(fileName, mimeType)) {
        return MediumType.MODEL_3D;
    }
    return MediumType.UNKNOWN;
}
export function getMediumType(fileName, mimeType) {
    return getMediaType(fileName, mimeType);
}
export function isPreviewableContentType(contentType) {
    return PREVIEWABLE_IMAGE_MIME_TYPES.includes(contentType);
}
export function isPreviewableFile(file) {
    return isPreviewableContentType(file.type);
}
// TODO: replace this with getMediumType -- or implement in terms of
/**
 * @deprecated Use getMediumType instead
 */
export function contentTypeToMediaType(contentType) {
    // NOTE: A "blank" content-type is assumed to be an image.  This is to
    // handle "RAW" images which many times don't have a content-type until
    // getting processed on the backend.  Browsers don't necessarily know how
    // to infer/assign the mime-type for RAW images which vary greatly
    if (contentType.startsWith('image/') || contentType === '') {
        return MediumType.IMAGE;
    }
    else if (PDF_MIME_TYPES.includes(contentType)) {
        return MediumType.DOCUMENT;
    }
    else if (WORD_MIME_TYPES.includes(contentType)) {
        return MediumType.DOCUMENT;
    }
    else if (EXCEL_MIME_TYPES.includes(contentType)) {
        return MediumType.DOCUMENT;
    }
    else if (POWERPOINT_MIME_TYPES.includes(contentType)) {
        return MediumType.DOCUMENT;
    }
    else if (contentType.startsWith('video/')) {
        return MediumType.VIDEO;
    }
    else if (contentType.startsWith('audio/')) {
        return MediumType.AUDIO;
    }
    else if (contentType.startsWith('model/')) {
        return MediumType.MODEL_3D;
    }
    else {
        return MediumType.UNKNOWN;
    }
}
export function isContentTypeWatermarkable(contentType) {
    if (contentType.startsWith('image/') || contentType === '') {
        return true;
    }
    else if (PDF_MIME_TYPES.includes(contentType)) {
        return true;
    }
    return false;
}
export function getDimensions(width, height) {
    return {
        portrait: height > width,
        width,
        height
    };
}
export function getScaledDimensions(dimensions, boundary) {
    const original_width = dimensions.width;
    const original_height = dimensions.height;
    const bound_width = boundary.width;
    const bound_height = boundary.height;
    let new_width = original_width;
    let new_height = original_height;
    // first check if we need to scale width
    if (original_width > bound_width) {
        // scale width to fit
        new_width = bound_width;
        // scale height to maintain aspect ratio
        new_height = (new_width * original_height) / original_width;
    }
    // then check if we need to scale with regards to the new height
    if (new_height > bound_height) {
        // scale height to fit instead
        new_height = bound_height;
        // scale width to maintain aspect ratio
        new_width = (new_height * original_width) / original_height;
    }
    return {
        portrait: dimensions.portrait,
        width: new_width,
        height: new_height
    };
}
function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}
export function getPixelCrop(width, height, relativeCrop) {
    // NOTE: the top/left can'be be less than 0
    const cropX = Math.max(relativeCrop.x, 0);
    const cropY = Math.max(relativeCrop.y, 0);
    const x = Math.round(cropX * width);
    const y = Math.round(cropY * height);
    const cropWidth = Math.round(relativeCrop.width * width);
    const cropHeight = Math.round(relativeCrop.height * height);
    return {
        x,
        y,
        // clamp width and height so rounding doesn't cause the crop to exceed bounds.
        width: clamp(cropWidth, 0, width - x),
        height: clamp(cropHeight, 0, height - y)
    };
}
export function convertPercentToDecimalCrop(relativeCrop) {
    // NOTE: the top/left can'be be less than 0
    const cropX = Math.max(relativeCrop.x, 0);
    const cropY = Math.max(relativeCrop.y, 0);
    return {
        x: cropX / 100,
        y: cropY / 100,
        width: relativeCrop.width / 100,
        height: relativeCrop.height / 100
    };
}
export function getCanvasPixelCrop(canvas, relativeCrop) {
    return getPixelCrop(canvas.width, canvas.height, relativeCrop);
}
export function rotateDimensions(dimensions, rotation) {
    if (rotation % 180 === 0) {
        return dimensions;
    }
    else {
        return {
            portrait: !dimensions.portrait,
            width: dimensions.height,
            height: dimensions.width
        };
    }
}
export function getPhysicalDerivativeDimensions(origWidth, origHeight, maxSize, density) {
    // image resizing is oriented to width... the theory is that width is what's pre-dominantly
    // perceived (at least in a left to right language), the driving factor, and the
    // dimension we are optimzing for (instead of image size).  There's an assumption that the
    // majority of images have an aspect ratio this works for.  For tall images that are narrow
    // this will result in possibly larger images than desired for the "normalized" size
    // calculate the desired new width and height based on the target size
    // fix the longer one to the desired length and adjust the other pair proportionally
    let newWidth, newHeight;
    newWidth = maxSize;
    newHeight = Math.round((maxSize / origWidth) * origHeight);
    // density calculation
    const targetDensity = density === '2x' ? 2 : 1;
    newWidth = newWidth * targetDensity;
    newHeight = newHeight * targetDensity;
    if (newWidth > origWidth || newHeight > origHeight) {
        newWidth = origWidth;
        newHeight = origHeight;
    }
    return [newWidth, newHeight];
}
const _getProp = (medium, size) => {
    let p;
    return (hiDPI && (p = medium[`${size}_2x`])
        ? p
        : medium[size]);
};
const _getImage = (medium, size) => {
    const derivatives = medium?.derivatives;
    if (derivatives) {
        const image = _getProp(derivatives, size);
        if (image) {
            return image;
        }
    }
    return {
        path: PLACEHOLDER_IMG,
        width: 225,
        height: 225
    };
};
export function getThumbnail(medium) {
    // NOTE: overriding to always use the 2x thumbnail if available
    // return _getImage(medium, 'thumbnail');
    const derivatives = medium?.derivatives;
    if (derivatives) {
        const image = derivatives.thumbnail_2x || derivatives.thumbnail;
        if (image) {
            return image;
        }
    }
    return {
        path: PLACEHOLDER_IMG,
        width: 225,
        height: 225
    };
}
export function getSmImage(medium) {
    return _getImage(medium, 'small');
}
export function getPublicThumbnail(medium) {
    return _getImage(medium, 'public_thumbnail');
}
export function getPublicImage(medium) {
    return _getImage(medium, 'public');
}
export function getPublicHighResImage(medium) {
    return _getImage(medium, 'public_highres');
}
export function getDominantColor(medium) {
    const derivatives = medium.derivatives;
    return (derivatives && derivatives.dominant_color) || 'ffffff'; // default to white
}
export function getComplimentaryColor(medium) {
    const derivatives = medium.derivatives;
    return (derivatives && derivatives.complementary_color) || 'ffffff'; // default to white
}
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        }
        : null;
}
// add a hash to the dst string depending on if the src string has one
const hashify = (src, dst) => `${src[0] === '#' ? '#' : ''}${dst}`;
export function getContrastColor(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return hashify(hex, 'FFFFFF');
    }
    // http://www.w3.org/TR/AERT#color-contrast
    const o = Math.round((rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000);
    // if the original string started with a # return the contrast with one
    return hashify(hex, o > 125 ? '000000' : 'FFFFFF');
}
export const getPlaceholderMedium = () => {
    const placeholder = getPlaceholderImage();
    return {
        uuid: 'placeholder-public',
        content_type: 'image/jpeg',
        path: PLACEHOLDER_IMG,
        derivatives: {
            dominant_color: 'ffffff',
            complementary_color: 'ffffff',
            full: placeholder,
            full_sq: placeholder,
            thumbnail: placeholder,
            thumbnail_2x: placeholder,
            small: placeholder,
            small_2x: placeholder,
            small_sq: placeholder,
            small_sq_2x: placeholder,
            public_thumbnail: placeholder,
            public_thumbnail_2x: placeholder,
            public: placeholder,
            public_2x: placeholder,
            public_highres: placeholder,
            public_highres_2x: placeholder
        }
    };
};
// This is really a "generic" placeholder to represent unknown media
// @deprecated: TODO: switch to getUnknownPlaceholder
export const getPlaceholderImage = () => ({
    path: PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getImagePlaceholder = () => ({
    path: IMAGE_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getPDFPlaceholder = () => ({
    path: PDF_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getWordPlaceholder = () => ({
    path: WORD_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getExcelPlaceholder = () => ({
    path: EXCEL_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getPowerPointPlaceholder = () => ({
    path: POWERPOINT_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getVideoPlaceholder = () => ({
    path: VIDEO_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const get3DModelPlaceholder = () => ({
    path: MODEL_3D_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getAudioPlaceholder = () => ({
    path: AUDIO_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getDocumentPlaceholder = () => ({
    path: DOCUMENT_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getUnknownPlaceholder = () => ({
    path: UNKNOWN_PLACEHOLDER_IMG,
    width: 600,
    height: 600
});
export const getMediumTypePlaceholder = (type = MediumType.UNKNOWN) => {
    switch (type) {
        case MediumType.DOCUMENT:
            return getDocumentPlaceholder();
        case MediumType.VIDEO:
            return getVideoPlaceholder();
        case MediumType.MODEL_3D:
            return get3DModelPlaceholder();
        case MediumType.AUDIO:
            return getAudioPlaceholder();
        case MediumType.IMAGE:
            return getImagePlaceholder();
        default:
            return getUnknownPlaceholder();
    }
};
export const getMediumPlaceholder = (type, mimeType) => {
    switch (type) {
        case MediumType.DOCUMENT:
            if (_isPDFFile('', mimeType)) {
                return getPDFPlaceholder();
            }
            else if (_isWordFile('', mimeType)) {
                return getWordPlaceholder();
            }
            else if (_isExcelFile('', mimeType)) {
                return getExcelPlaceholder();
            }
            else if (_isPowerPointFile('', mimeType)) {
                return getPowerPointPlaceholder();
            }
            else {
                return getDocumentPlaceholder();
            }
        case MediumType.VIDEO:
            return getVideoPlaceholder();
        case MediumType.MODEL_3D:
            return get3DModelPlaceholder();
        case MediumType.AUDIO:
            return getAudioPlaceholder();
        case MediumType.IMAGE:
            return getImagePlaceholder();
        default:
            return getUnknownPlaceholder();
    }
};
export const equalRectangles = (r1, r2) => {
    return (
    // if neither rectangle exists then they're equal
    (!r1 && !r2) ||
        (!!r1 &&
            !!r2 &&
            r1.x === r2.x &&
            r1.y === r2.y &&
            r1.width === r2.width &&
            r1.height === r2.height));
};
export const equalTransforms = (t1, t2) => {
    return (
    // if neither transform exists then they're equal
    (!t1 && !t2) ||
        (!!t1 &&
            !!t2 &&
            t1.rotation === t2.rotation &&
            equalRectangles(t1.relativeCrop, t2.relativeCrop) &&
            equalRectangles(t1.relativeThumbnail, t2.relativeThumbnail)));
};
//# sourceMappingURL=media-utils.js.map